import {
	AttachmentDto,
	TopographyMetadataDto,
	MetadataDetailDto,
	MetadataDto,
	PaginatedListOfMetadataDto
} from './../../models/models';
import { AppThunkAction, AppThunkDispatch, AppAction } from './../app.types';
import {
	Metadata,
	MetadataAction,
	MetadataActions,
	MetadataAttachmentAction,
	MetadataAttachmentListAction,
	MetadataByPageAction as TopographyMetadataAction,
	MetadataListAction
} from './metadata.types';
import {
	getAllMetadata,
	getMetadata,
	getMetadataByPage,
	getMetadataCategories,
	postMetadata,
	putMetadata,
	removeMetadata,
	uploadTopographyData
} from '../../services/MetadataService';
import { history } from '../Store';
import { Asset } from '../assets/asset.types';
import { Region } from '../regions/region.types';
import { Theme } from '../themes/theme.types';
import moment from 'moment';
import { PagingActions, PagingOptions } from '../../models/types';
import { AppState } from '../app.state';
import { Attachment } from '../projects/projects.types';
import { Chapter } from '../chapters/chapter.types';
import produce from 'immer';
import { AttachmentEditorAction } from '../attachment-editor/attachment-editor.types';
import { ATTACHMENT_EDITOR_UPDATE_PIN_POSITION } from '../attachment-editor/attachment-editor.actions';
import { getMapTitle, getQueryString } from '../../utils';
import { UploadFileAction } from '../upload/upload.types';
import { upload } from '../../services';

export const METADATA_CREATE: string = 'METADATA_CREATE';

export const METADATA_FETCH_LIST: string = 'METADATA_FETCH_LIST';
export const METADATA_FETCH: string = 'METADATA_FETCH';

export const METADATA_UPDATE: string = 'METADATA_UPDATE';

export const METADATA_DELETE: string = 'METADATA_DELETE';

export const METADATA_LOAD_DEFAULT: string = 'METADATA_LOAD_DEFAULT';
export const METADATA_LOAD_LIST: string = 'METADATA_LOAD_LIST';
export const METADATA_LOAD: string = 'METADATA_LOAD';
export const METADATA_LOAD_BY_PAGE: string = 'METADATA_LOAD_BY_PAGE';

export const METADATA_ADD_ATTACHMENT: string = 'METADATA_ADD_ATTACHMENT';
export const METADATA_UPDATE_ATTACHMENT: string = 'METADATA_UPDATE_ATTACHMENT';
export const METADATA_UPDATE_ATTACHMENTS: string =
	'METADATA_UPDATE_ATTACHMENTS';

export const METADATA_CHANGE_PAGE: string = 'METADATA_CHANGE_PAGE';
export const METADATA_LOAD_LIST_OPTIONS: string = 'METADATA_LOAD_LIST_OPTIONS';

export const METADATA_FETCH_CATEGORIES: string = 'METADATA_FETCH_CATEGORIES';
export const METADATA_LOAD_CATEGORIES: string = 'METADATA_LOAD_CATEGORIES';

export const METADATA_UPLOAD_TOPOGRAPHY_DATA: string =
	'METADATA_UPLOAD_TOPOGRAPHY_DATA';

export function createMetadata(
	metadata: Metadata
): AppThunkAction<void, MetadataAction> {
	return async (dispatch: AppThunkDispatch<MetadataAction>) => {
		dispatch({ type: METADATA_CREATE });
		const newMetadataId: string = await postMetadata(
			filterMetadataSpaceFields(metadata)
		);
		const newMetadata: MetadataDetailDto = await getMetadata(newMetadataId);

		history.replace(`/manage/metadata/${newMetadataId}`);
		dispatch(loadMetadata(newMetadata));
	};
}

export function fetchAllMetadata(
	pageIndex: number = 0,
	pageSize: number = 50
): AppThunkAction<void, MetadataActions | PagingActions> {
	return async (
		dispatch: AppThunkDispatch<MetadataActions | PagingActions>,
		getState: () => AppState
	) => {
		dispatch({ type: METADATA_FETCH_LIST });

		const state: AppState = getState();
		const queryString: string = getQueryString(
			pageIndex,
			pageSize,
			state.contentManager.sort,
			state.contentManager.filter
		);

		const pagedList: PaginatedListOfMetadataDto = await getAllMetadata(
			queryString
		);
		const metadataList: MetadataDto[] = pagedList.items as MetadataDto[];
		const pagingOptions: PagingOptions = {
			currentPage: pagedList.pageIndex,
			totalPages: pagedList.totalPages,
			pageSize: pageSize
		};

		dispatch(loadMetadataList(metadataList));
		dispatch({ type: METADATA_LOAD_LIST_OPTIONS, payload: pagingOptions });
	};
}

export function fetchMetadata(
	id: string
): AppThunkAction<void, MetadataAction> {
	return async (dispatch: AppThunkDispatch<MetadataAction>) => {
		dispatch({ type: METADATA_FETCH });

		const metadata: MetadataDetailDto = await getMetadata(id);

		if (metadata.attachments) {
			metadata.attachments = metadata.attachments.sort(
				(attachment1: AttachmentDto, attachment2: AttachmentDto) => {
					return attachment1.order > attachment2.order ? 1 : -1;
				}
			);
		}

		dispatch(loadMetadata(metadata));
	};
}

export function fetchMetadataByPage(
	page: number,
	letter: string
): AppThunkAction<void, TopographyMetadataAction> {
	return async (dispatch: AppThunkDispatch<TopographyMetadataAction>) => {
		dispatch({ type: METADATA_FETCH });

		const metadata: TopographyMetadataDto = await getMetadataByPage(
			page,
			letter
		);

		dispatch({ type: METADATA_LOAD_BY_PAGE, payload: metadata });
	};
}

export function updateMetadata(
	metadata: Metadata
): AppThunkAction<void, MetadataAction> {
	return async (dispatch: AppThunkDispatch<MetadataAction>) => {
		dispatch({ type: METADATA_UPDATE });
		await putMetadata(filterMetadataSpaceFields(metadata));
		const updatedMetadata: MetadataDetailDto = await getMetadata(
			metadata.id as string
		);
		dispatch(loadMetadata(updatedMetadata));
	};
}

export function deleteMetadata(
	id: string
): AppThunkAction<void, MetadataAction> {
	return async (dispatch: AppThunkDispatch<MetadataAction>) => {
		dispatch({ type: METADATA_DELETE });
		await removeMetadata(id);
		dispatch(fetchAllMetadata());
	};
}

export function metadataFetchCategories(): AppThunkAction<
	void,
	AppAction<string[]>
> {
	return async (dispatch: AppThunkDispatch<AppAction<string[]>>) => {
		dispatch({ type: METADATA_FETCH_CATEGORIES });
		const categories: string[] = await getMetadataCategories();
		dispatch({ type: METADATA_LOAD_CATEGORIES, payload: categories });
	};
}

export function metadataAddAttachment(
	attachment: Attachment,
	file?: File
): AppThunkAction<void, MetadataAttachmentAction> {
	return async (dispatch: AppThunkDispatch<MetadataAttachmentAction>) => {
		if (file) {
			const uploadResult = await upload(`file`, file);
			attachment.location = uploadResult.data;
		}

		dispatch({
			type: METADATA_ADD_ATTACHMENT,
			payload: attachment
		});
	};
}

export function metadataUpdateAttachment(
	attachment: Attachment,
	file?: File
): AppThunkAction<void, MetadataAttachmentAction> {
	return async (dispatch: AppThunkDispatch<MetadataAttachmentAction>) => {
		if (file) {
			const uploadResult = await upload(`file`, file);
			attachment.location = uploadResult.data;
		}
		dispatch({
			type: METADATA_UPDATE_ATTACHMENT,
			payload: attachment
		});
	};
}

export function metadataUpdateAttachments(
	attachments: Attachment[]
): MetadataAttachmentListAction {
	return {
		type: METADATA_UPDATE_ATTACHMENTS,
		payload: attachments.map((attachment: Attachment, index: number) => {
			return produce(attachment, (draft: Attachment) => {
				draft.order = index + 1;
			});
		})
	};
}

export function metadataMoveAttachment(
	attachment: Attachment
): AppThunkAction<void, AttachmentEditorAction> {
	return (
		dispatch: AppThunkDispatch<
			AttachmentEditorAction | MetadataAttachmentAction
		>
	) => {
		dispatch({
			type: ATTACHMENT_EDITOR_UPDATE_PIN_POSITION,
			payload: attachment
		});
		dispatch(metadataUpdateAttachment(attachment));
	};
}

export function changeMetadataPage(
	page: number
): AppThunkAction<void, PagingActions> {
	return (
		dispatch: AppThunkDispatch<PagingActions>,
		getState: () => AppState
	) => {
		const state = getState();
		dispatch({ type: METADATA_CHANGE_PAGE, payload: page });

		dispatch(fetchAllMetadata(page, state.metadata.pagingOptions.pageSize));
	};
}

export function metadataUploadTopographicData(
	file: File
): AppThunkAction<void, UploadFileAction> {
	return async (dispatch: AppThunkDispatch<UploadFileAction>) => {
		dispatch({ type: METADATA_UPLOAD_TOPOGRAPHY_DATA });
		await uploadTopographyData(file);
	};
}

export function loadDefaultMetadata(): MetadataAction {
	return {
		type: METADATA_LOAD_DEFAULT
	};
}

export function loadMetadataList(dtoList: MetadataDto[]): MetadataListAction {
	const metadataList: Metadata[] = dtoList.map(metadataDtoToMetadata);
	return {
		type: METADATA_LOAD_LIST,
		payload: metadataList
	};
}

export function loadMetadata(dto: MetadataDetailDto): MetadataAction {
	const metadata: Metadata = metadataDetailDtoToMetadata(dto);
	return {
		type: METADATA_LOAD,
		payload: metadata
	};
}

function metadataDetailDtoToMetadata(dto: MetadataDetailDto): Metadata {
	const licenceIds: string[] | undefined = dto.licences?.map(({ id }) => id);
	return {
		...dto,
		id: dto.id,
		title: dto.title as string,
		aliasses: dto.aliasses as string,
		generatedAliasses: dto.generatedAliasses,
		category: dto.category as string,
		dateOfMap: moment(dto.dateOfMap).format('yyyy-MM-DD'),
		isDigitalOnly: dto.isDigitalOnly,
		pageNumber: dto.pageNumber,
		pageSection: dto.pageSection as string,
		assetId: dto.asset?.id as string,
		asset: dto.asset as Asset,
		chapterId: dto.chapter?.id as string,
		chapter: dto.chapter as Chapter,
		topicChapterId: dto.topicChapter ? dto.topicChapter.id : ' ',
		topicChapter: dto.topicChapter as Chapter,
		regionId: dto.region ? dto.region.id : ' ',
		region: dto.region as Region,
		themeId: dto.theme ? dto.theme.id : ' ',
		theme: dto.theme as Theme,
		chapterThemeId: dto.chapterTheme ? dto.chapterTheme.id : ' ',
		chapterTheme: dto.chapterTheme as Theme,
		licenceIds: licenceIds ? licenceIds : [],
		attachments: dto.attachments as Attachment[],
		showUnderChapter: dto.showUnderChapter,
		showUnderRegion: dto.showUnderRegion
	};
}

function metadataDtoToMetadata(dto: MetadataDto): Metadata {
	return {
		...dto,
		id: dto.id,
		title: dto.title as string,
		aliasses: '',
		assetId: '',
		category: '',
		dateOfMap: '',
		isDigitalOnly: dto.isDigitalOnly,
		pageNumber: dto.pageNumber,
		pageSection: dto.pageSection ? dto.pageSection : '',
		regionId: '',
		licenceIds: [],
		attachments: [],
		showUnderChapter: false,
		showUnderRegion: false,
		region: undefined,
		chapter: undefined,
		displayTitle: getMapTitle(dto)
	};
}

function filterMetadataSpaceFields(metadata: Metadata): Metadata {
	return {
		...metadata,
		chapterId: metadata.chapterId === ' ' ? undefined : metadata.chapterId,
		topicChapterId:
			metadata.topicChapterId === ' '
				? undefined
				: metadata.topicChapterId,
		regionId: metadata.regionId === ' ' ? undefined : metadata.regionId,
		themeId: metadata.themeId === ' ' ? undefined : metadata.themeId,
		chapterThemeId:
			metadata.chapterThemeId === ' '
				? undefined
				: metadata.chapterThemeId
	};
}
